import { useState, useEffect, useRef } from "react";
import { Modal } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { numberToWords } from "../../../../utils/numberToWords";
import sortBy from "lodash/sortBy";
import dayjs from "dayjs";
import db from "../../../../database";
import upsertPOSLog from "../../Retail/posLog";
import useCartStore from "../../../../stores/useCartStore";
import usePOSConfigStore from "../../../../stores/usePOSConfigStore";
import useTransactionStore from "../../../../stores/useTransactionStore";
import useConfigStore from "../../../../stores/useConfigStore";

/**
 * useCoreEffects
 *
 * Extracts large useEffect blocks and notes-related handlers from PointOfsaleCore:
 * - Cart total / amountInWords effect
 * - Sale type sync effect
 * - Pre-order cart check effect
 * - Cart payment methods sync effect
 * - Store-credit wallet auto-select effect
 * - Auto-process payment effect
 * - Notes handlers (onAddNotes, handleKeyDown, onChangeInputKeyboard, onChange, handleShift)
 * - Keyboard handler effects (activeInput focus, keydown listener)
 * - Product stock timer effect
 * - Currency fill + sale types init effect
 * - Hold queue localStorage sync effect
 */
const useCoreEffects = ({
  cart, setCart, isExchange, precision, tillData, tillLayout, posConfig,
  history, tillaccess,
  // Notes
  notesPopup, notesValue, setNotesValue, exchangeNotesValue, setExchangeNotesValue, activeCartTable, setActiveCartTable, selectedProductForNotes, setSelectedProductForNotes,
  setNotesPopup, setIsInputFocused, keyboardRef, layoutName, setLayoutName,
  // Cart effects
  setOverPayedAmount, setSelectedProductInCart, setGroupQty,
  showPaymentMethods, setShowPaymentMethods, setPaymentProcessFlag,
  getFilteredPaymentMethods, setShowPayments, setStartIndex,
  amount, selectedPaymentMethod, isNewGiftCardFlag, showCustomerWallet,
  processOrder,
  // Active input
  activeInput, searchInputRef, qtyInputRef,
  // Product stock
  productStock, setProductStock,
  // Currency + sale types
  currenciesData, setCurrenciesData, setPosSaleTypes, setOrderType,
  // Hold queue
  holdQueue,
  // Active tab
  activeTabName, setActiveTabName, retailCustomerFields,
  // Selected sale type
  selectedSaleType,
  // Wallet
  isRefundWalletOpen, setSelectedPaymentMethod,
  // Cart amount/order
  setAmount, setOrderObj,
  // Zone navigation
  suppressAutoFocusRef,
}) => {
  const orderState = useRef(0);
  const isInitialMount = useRef(true);

  // Notes handlers
  const onAddNotes = () => {
    let updatedCart;
    const activeCart = activeCartTable === "EXCHANGE" ? useCartStore.getState().exchangeCart : cart;
    if (selectedProductForNotes !== null && activeCart.items.length > 0) {
      updatedCart = { ...activeCart };
      let index = updatedCart.items.findIndex((res) => res.lineId === selectedProductForNotes.lineId);
      if (index >= 0) {
      updatedCart.items[index].notes = activeCartTable === "EXCHANGE" ? exchangeNotesValue : notesValue;
      }
    if (activeCartTable === "EXCHANGE") {
      setExchangeNotesValue("");
    } else {
      setNotesValue("");
    }
    } else {
    updatedCart = {
      ...activeCart,
      description:
        activeCartTable === "EXCHANGE"
          ? exchangeNotesValue
          : notesValue,
    };
    }
    setSelectedProductForNotes(null);
    if (activeCartTable === "EXCHANGE") {
      useCartStore.getState().setExchangeCart(updatedCart);
    } else {
      setCart(updatedCart);
    }
    setNotesPopup(false);
    setIsInputFocused(false);
    let orderTimeDetails = usePOSConfigStore.getState().orderTimeDetails || { orderStartTime: "", orderEndTime: "", paymentStartTime: "" };
    if (updatedCart.items.length === 0 || tillData.tillAccess.csBunit.b2cCustomer.cwrCustomerId === updatedCart.customer.cwrCustomerId) {
      orderTimeDetails = {
        ...orderTimeDetails,
        paymentStartTime: "",
        orderStartTime: dayjs(new Date()).format("DD-MM-YYYY HH:mm:ss"),
      };
    } else if (updatedCart.items.length > 0 || tillData.tillAccess.csBunit.b2cCustomer.cwrCustomerId !== updatedCart.customer.cwrCustomerId) {
      orderTimeDetails = {
        ...orderTimeDetails,
        paymentStartTime: "",
      };
    }
    if (updatedCart.description === "") {
      orderTimeDetails = {
        ...orderTimeDetails,
        paymentStartTime: "",
        orderStartTime: "",
      };
    }
    usePOSConfigStore.getState().setOrderTimeDetails(orderTimeDetails);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onAddNotes();
    }
  };

  const onChangeInputKeyboard = (event) => {
    const input = event.target.value;

    if (activeCartTable === "EXCHANGE") {
      setExchangeNotesValue(input);
    } else {
      setNotesValue(input);
    }
    if (keyboardRef.current) {
      keyboardRef.current.setInput(input);
    }
  };

  const onChange = (input) => {
    if (input !== "Enter") {
    if (activeCartTable === "EXCHANGE") {
      setExchangeNotesValue(input);
    } else {
      setNotesValue(input);
    }
    }
  };

  const handleShift = () => {
    layoutName === "default" ? setLayoutName("shift") : setLayoutName("default");
  };

  // Active tab name default
  useEffect(() => {
    if (activeTabName === "") {
      setActiveTabName("contact");
    }
  }, [retailCustomerFields]);

  // Notes popup focus
  useEffect(() => {
    const notesValueInput = document.getElementById("notes-input");
    if (notesPopup) {
      notesValueInput?.focus();
    } else {
      notesValueInput?.blur();
      setIsInputFocused(false);
    }
  }, [notesPopup]);

  // Hold queue localStorage sync
  useEffect(() => {
    localStorage.setItem("callCenterHoldQueue", JSON.stringify(holdQueue));
  }, [holdQueue]);

  // Active input focus + keydown listener
  useEffect(() => {
    const handleKeyDownGlobal = (event) => {
      const activeElement = document.activeElement;
      const isInputFocused = activeElement && (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA" || activeElement.isContentEditable);
      // BUG-1185: Also detect weight/UOM popups that use getContainer={false} (renders inside parent, not body)
      const isDrawerOrModalOpen = !!document.querySelector(".ant-drawer-open") || !!document.querySelector(".ant-modal-mask") || document.body.classList.contains("ant-modal-open") || !!document.querySelector(".ant-modal-wrap:not([style*='display: none'])");
      if (!isInputFocused && !isDrawerOrModalOpen && !event.ctrlKey && !event.altKey && !event.metaKey && !suppressAutoFocusRef?.current) {
        if (event.key.length === 1 || event.key === "Backspace" || event.key === "Delete" || event.key === "ArrowLeft" || event.key === "ArrowRight") {
          if (typeof setActiveInput === "function") setActiveInput("search");
          if (searchInputRef?.current) {
            searchInputRef?.current?.focus();
          }
        }
      }
    };

    if (activeInput === "search") {
      if (searchInputRef?.current) {
        searchInputRef?.current?.focus();
      }
    } else if (activeInput === "weightPopup") {
      const productSearchInput = document.getElementById("sm-weight-popup-input");
      if (productSearchInput) {
        productSearchInput.focus();
      }
    } else if (activeInput === "qty") {
      qtyInputRef.current?.focus();
    }

    document.addEventListener("keydown", handleKeyDownGlobal);
    return () => {
      document.removeEventListener("keydown", handleKeyDownGlobal);
    };
  }, [activeInput]);

  // Product stock timer
  useEffect(() => {
    if (productStock) {
      console.debug("Product stock is low");
      setProductStock(true);
      const timer = setTimeout(() => {
        setProductStock(false);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [productStock]);

  // Currency fill + sale types init
  useEffect(() => {
    const totalRecords = 12;
    let filledCurrencies = [...currenciesData];
    while (filledCurrencies.length < totalRecords) {
      filledCurrencies.push({ id: `placeholder-${filledCurrencies.length}`, value: 999999 });
    }
    setCurrenciesData(filledCurrencies.sort((a, b) => b.value - a.value));
    useConfigStore.getState().loadConfig();
    db.posSaletypes.toArray().then((saleType) => {
      setPosSaleTypes([...saleType]);
      const saleIndex = saleType.findIndex((st) => st.cwrSaletype.isdefault === "Y");
      setOrderType(saleType[saleIndex]);
    });
  }, []);

  // Cart total / amountInWords effect
  const precisionGuardRef = useRef(0);
  useEffect(() => {
    if (cart?.items?.length > 0) {
      const preciseTotal = parseFloat(cart?.total?.toFixed(precision));
      if (preciseTotal !== cart?.total && Math.abs(preciseTotal - cart?.total) > 1e-10) {
        if (precisionGuardRef.current >= 3) {
          precisionGuardRef.current = 0;
          return;
        }
        precisionGuardRef.current += 1;
        let updatedCart = {
          ...cart,
          total: preciseTotal,
          amountInWords: numberToWords(preciseTotal),
          totalStd:
            cart?.items?.reduce((sum, item) => {
              if (item?.isCanceled === "Y") return sum;
              if (Array.isArray(item.children) && item.children.length > 0) {
                const childrenStd = item.children.reduce((childSum, child) => childSum + (child?.linestd || 0), 0);
                return sum + childrenStd;
              }
              return sum + (item?.linestd || 0);
            }, 0) || 0,
        };
        setCart(updatedCart);
      } else {
        precisionGuardRef.current = 0;
      }
    } else {
      precisionGuardRef.current = 0;
    }
    // Exchange cart precision
    const exchangeCartObj = useCartStore.getState().exchangeCart;
    if (isExchange && exchangeCartObj?.items?.length > 0) {
      const exchPreciseTotal = parseFloat(exchangeCartObj?.total?.toFixed(precision));
      if (exchPreciseTotal !== exchangeCartObj?.total) {
        const updatedExchCart = {
          ...exchangeCartObj,
          total: exchPreciseTotal,
          amountInWords: numberToWords(exchPreciseTotal),
          totalStd:
            exchangeCartObj?.items?.reduce((sum, item) => {
              if (item?.isCanceled === "Y") return sum;
              if (Array.isArray(item.children) && item.children.length > 0) {
                const childrenStd = item.children.reduce((childSum, child) => childSum + (child?.linestd || 0), 0);
                return sum + childrenStd;
              }
              return sum + (item?.linestd || 0);
            }, 0) || 0,
        };
        localStorage.setItem("exchangeCartObj", JSON.stringify(updatedExchCart));
        useCartStore.getState().setExchangeCart(updatedExchCart);
      }
    }
  }, [cart, isExchange]);

  // Sale type sync effect
  useEffect(() => {
    let saleType = usePOSConfigStore.getState().saleTypeData;
    let cartObj = useCartStore.getState().cart || cart;
    if (saleType && cartObj?.items?.length > 0) {
      let updatedCart = {
        ...cartObj,
        saleType: saleType?.cwrSaletype,
      };
    }
  }, [selectedSaleType, cart]);

  // Pre-order cart check effect
  useEffect(() => {
    if (cart?.items?.length > 0) {
      if (window.location.pathname.includes("/pos")) {
        if (cart?.isPreOrderPage === true && cart?.preOrder !== "Y" && tillaccess?.layout !== "2") {
          Modal.confirm({
            title: "Clear The Cart Items ?",
            icon: <ExclamationCircleOutlined />,
            content: <div>Pre-Order cart will be cleared if you continue. Do you want to proceed?</div>,
            okText: "Yes",
            cancelText: "No",
            autoFocusButton: null,
            okButtonProps: { style: { display: "inline-block" } },
            onOk() {
              let updatedCart = {
                ...cart,
                isPreOrderPage: false,
                items: [],
                total: 0,
                subTotal: 0,
                tax: 0,
                discount: 0,
                paid: 0,
                change: 0,
                roundOff: 0,
                advancePayment: 0,
                couponInput: [],
                payments: [],
                giftCardRefId: null,
                couponRefId: null,
                itemsSummery: {},
                groupedItemsByTaxRate: {},
              };
              if (!isExchange) {
                setCart(updatedCart);
              }
            },
            onCancel() {
              history.push("/preorder");
            },
          });
        }
      } else if (window.location.pathname.includes("/preorder") && !cart.isPreOrderPage) {
        let updatedCart = {
          ...cart,
          isPreOrderPage: true,
        };
      }
    }
  }, [cart]);

  // Cart payment methods sync effect
  useEffect(() => {
    let cartObj = useCartStore.getState().cart || cart;
    // cartRef.current = { ...cart }; — done in caller
    if (cart.items.length === 0) {
      setOverPayedAmount(0);
      setSelectedProductInCart({});
      setGroupQty(1);
      setShowPaymentMethods(false);
      // Only call setCart if there's actually something to reset (avoid infinite loop)
      if (cart.couponInput?.length > 0 || cart.discount > 0 || cart.payments?.length > 0 || cart.paid > 0 || cart.giftCardRefId || cart.totalBillDicount) {
        setCart({ ...cart, couponInput: [], discount: 0, payments: [], paid: 0, giftCardRefId: null, totalBillDicount: null });
      }
    }
    let PaymentMethods = getFilteredPaymentMethods() || [];
    const isPreOrderPage = window.location.pathname.includes("/preorder");
    const advanceItem = cart.items.find((item) => item.type === "S" && item.weight > 0 && item.productSegment === "AD");
    if (
      cart.layAway === "Y" &&
      cart?.docType === "Advance" &&
      !advanceItem &&
      posConfig?.allowLayawaywithoutPayment !== "Y" &&
      cart.items.some((item) => item.type !== "S" && item?.productSegment !== "AD")
    ) {
      PaymentMethods = [
        ...PaymentMethods,
        ...(posConfig?.enablePreOrder !== "Y"
          ? [
              {
                cWRPaymentMethodID: "",
                sequenceNo: 9999,
                finPaymentmethodId: "",
                finFinancialAccountId: "",
                finDayCloseAccountId: null,
                name: "Complete Order",
                integratedPayment: false,
                isloyalty: false,
                paymentProvider: null,
                iscredit: false,
                isGiftCard: false,
                isDefault: "N",
                csCurrencyId: "",
                isoCode: "AED",
                isactive: true,
                salesType: [],
              },
            ]
          : []),
      ];
      let payments =
        PaymentMethods.length > 5
          ? sortBy(PaymentMethods, "sequenceNo").slice(
              0,
              tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId === cart?.customer?.cwrCustomerId && PaymentMethods.length === 5 ? 5 : (cart.layAway === "Y" || cart?.creditSale === "Y") ? 4 : 5
            )
          : PaymentMethods;
      setShowPayments(payments);
      setStartIndex((cart.layAway === "Y" || cart?.creditSale === "Y") && payments.length >= 5 ? 5 : payments.length);
    } else if (isPreOrderPage) {
      const isRefundScenario = amount < 0 && selectedPaymentMethod?.name?.toLowerCase() === "cash";
      const maxPaymentsToShow = isRefundScenario ? 3 : 4;
      let payments =
        PaymentMethods.length > maxPaymentsToShow
          ? sortBy(PaymentMethods, "sequenceNo").slice(
              0,
              tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId === cart?.customer?.cwrCustomerId && PaymentMethods.length === maxPaymentsToShow
                ? maxPaymentsToShow
                : cart.layAway === "Y"
                  ? maxPaymentsToShow
                  : maxPaymentsToShow
            )
          : PaymentMethods;
      setShowPayments(payments);
      setStartIndex(cart.layAway === "Y" && payments.length >= maxPaymentsToShow ? maxPaymentsToShow : payments.length);
    } else if (!showPaymentMethods) {
      let payments =
        PaymentMethods.length > 5
          ? sortBy(PaymentMethods, "sequenceNo").slice(
              0,
              tillData?.tillAccess?.csBunit?.b2cCustomer?.cwrCustomerId === cart?.customer?.cwrCustomerId && PaymentMethods.length === 5 ? 5 : (cart.layAway === "Y" || cart?.creditSale === "Y") ? 4 : 5
            )
          : PaymentMethods;
      setShowPayments(payments);
      setStartIndex((cart.layAway === "Y" || cart?.creditSale === "Y") && payments.length >= 5 ? 5 : payments.length);
    }
  }, [cart, selectedSaleType]);

  // Store-credit wallet auto-select effect
  const selectedReturnTypeReactive = useTransactionStore((s) => s.selectedReturnType);
  useEffect(() => {
    const cartOBJ = useCartStore.getState().cart;
    if (selectedReturnTypeReactive === "STORE_CREDIT" && cartOBJ?.customer?.cwrWallet?.wallet_Id) {
      const walletPayment = tillData.tillAccess.csBunit.paymentMethodList.find((pm) => pm.isWallet === "Y");
      if (walletPayment) {
        setSelectedPaymentMethod(walletPayment);
      }
    }
  }, [cart, isRefundWalletOpen, selectedReturnTypeReactive]);

  // Auto-process payment / cart amount effect
  useEffect(() => {
    const isExchangeMode = useTransactionStore.getState().selectedReturnType === "EXCHANGE";
    const exchangeCartObj = isExchangeMode ? (useCartStore.getState().exchangeCart || {}) : null;
    const baseTotal = isExchangeMode
      ? Math.abs((exchangeCartObj.total || 0) + cart.total)
      : Math.abs(cart.total);
    const remaining = parseFloat(
      (baseTotal - (cart.paid ?? 0) + (cart?.deiveryCharge ?? 0) + (cart?.tip ?? 0) + (cart?.serviceCharge ?? 0) + (cart?.packingCharge ?? 0)).toFixed(precision)
    );
    const isReturnPaymentActive = (cart.isReturn || isExchangeMode) && showPaymentMethods;
    if (!isReturnPaymentActive && !(cart.payments?.length > 0 && cart.isReturn)) {
      setAmount(remaining);
    }
    // Skip auto-processing on initial mount (prevents re-processing stale cart from SQLite)
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else if (isExchangeMode) {
      let flag = tillLayout !== 1 ? true : false;
      const exchTotal = Math.abs(exchangeCartObj?.total || 0);
      const retTotal = Math.abs(cart.total || 0);
      const exchPaid = Number(exchangeCartObj?.paid) || 0;
      const netPayable = exchTotal - retTotal - exchPaid;
      if (netPayable <= 0.01 && cart.payments?.length > 0 && flag) {
        orderState.current = 0;
        setPaymentProcessFlag(false);
        processOrder(cart.sOrderID);
      }
    } else {
      let flag = tillLayout !== 1 ? true : false;
      if (Math.abs(cart.total) <= cart.paid && cart.payments.length > 0 && flag && cart.layAway === "Y" && posConfig?.allowLayawaywithoutPayment === "Y") {
        orderState.current = 0;
        setPaymentProcessFlag(false);
        processOrder(cart.sOrderID);
      } else if (
        Math.abs(cart.total) <= cart.paid &&
        cart.payments.length > 0 &&
        flag &&
        (cart.layAway !== "Y" || cart.preOrder === "Y") &&
        !isNewGiftCardFlag &&
        !showCustomerWallet
      ) {
        orderState.current = 0;
        setPaymentProcessFlag(false);
        processOrder(cart.sOrderID);
      }
    }
    try {
      const cartDetails = useCartStore.getState().cart;
      if (cartDetails !== null) {
        setOrderObj(cartDetails);
      }
    } catch (error) {
      console.error("Error parsing cart details:", error);
    }
    if (cart.discount) {
      upsertPOSLog(cart, "DAP");
    }
  }, [cart]);

  return {
    onAddNotes,
    handleKeyDown,
    onChangeInputKeyboard,
    onChange,
    handleShift,
    orderState,
  };
};

export default useCoreEffects;
